// Ein Auftrag des Dokumenten-Assistenten in der Liste (seit 21.10.2026):
// Kopf mit Deal und Status, der laufende Schritt samt Fortschritt, ein Fehler
// gut sichtbar, darunter das Ergebnis und die Knoepfe "Erneut versuchen" und
// "Entfernen". Ein haengender Auftrag (der Server meldet `haengend`) zeigt
// "Hängt" und bietet beide Knoepfe an.

import React from 'react';
import { Bot, Briefcase, FileText, RotateCcw, Trash2 } from 'lucide-react';
import { T, karteStil, knopfStil, markeStil } from '../../../lib/designTokens';
import {
  anzeigeStatus, fortschrittAnteil, groesseText, istHaengend, istOffen, kannEntfernen, kannWiederholen,
  vertragsartLabel, zeitText, zusammenfassung,
} from '../../../lib/dokumentenAssistent';
import { AuftragErgebnis, Hinweis, StatusMarke } from './AuftragDetails';

const HINWEIS_TON = { fehler: 'gefahr', pruefen: 'warn', haengend: 'warn' };

function Fortschrittsbalken({ anteil }) {
  return (
    <div
      role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={anteil}
      aria-label={`${anteil} % der Seiten gelesen`}
      style={{ height: 6, borderRadius: 999, background: T.hairline, overflow: 'hidden' }}
    >
      <div style={{ width: `${anteil}%`, height: '100%', background: T.orange, transition: 'width 300ms ease' }} />
    </div>
  );
}

function Herkunft({ auftrag }) {
  const teile = [
    vertragsartLabel(auftrag.vertragsart),
    auftrag.quelle === 'deal' ? 'Vertrag aus dem Deal' : (auftrag.dateiname || 'Hochgeladene Datei'),
    auftrag.quelle !== 'deal' && auftrag.groesse ? groesseText(auftrag.groesse) : '',
    zeitText(auftrag.erstelltAm),
    auftrag.erstelltVon || '',
  ].filter(Boolean);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: T.tMikro, color: T.muted, flexWrap: 'wrap' }}>
      <FileText size={11} />
      <span>{teile.join(' · ')}</span>
    </div>
  );
}

/**
 * auftrag: im Format des Servers; aktion: was gerade fuer diesen Auftrag laeuft
 * ('wiederholen' | 'entfernen' | ''); fehler: Meldung der letzten Aktion.
 */
export default function AuftragKarte({ auftrag, aktion = '', fehler = '', onWiederholen, onEntfernen }) {
  const offen = istOffen(auftrag);
  const status = anzeigeStatus(auftrag);
  const anteil = auftrag.status === 'laeuft' && !istHaengend(auftrag) ? fortschrittAnteil(auftrag) : null;
  const beschaeftigt = Boolean(aktion);
  const titel = auftrag.dealTitel ? ` · ${auftrag.dealTitel}` : '';

  return (
    <article style={{ ...karteStil, padding: '14px 16px', display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'grid', gap: 4 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7, fontSize: T.tTitel, fontWeight: 700, color: T.navy,
            letterSpacing: T.spurTitel, minWidth: 0,
          }}>
            <Briefcase size={13} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Deal {auftrag.dealId || '–'}{titel}
            </span>
          </div>
          <Herkunft auftrag={auftrag} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {auftrag.art === 'automatik' ? (
            <span style={markeStil(T.navy, T.rand, T.flaeche)} title="Von der Bitrix-Automatik „Vertrag Digital vorhanden“ angelegt">
              <Bot size={11} /> Automatik
            </span>
          ) : null}
          {auftrag.nurAnalyse ? (
            <span style={markeStil(T.info, T.infoRand, T.flaeche)} title="Nur erkannt, nichts hochgeladen">Probelauf</span>
          ) : null}
          <StatusMarke status={status} />
        </div>
      </div>

      {/* Fehler, "von Hand pruefen" und "haengt" stehen farbig da, nicht im Fliesstext. */}
      {HINWEIS_TON[status] ? (
        <Hinweis ton={HINWEIS_TON[status]}>{zusammenfassung(auftrag)}</Hinweis>
      ) : (
        <div style={{ fontSize: T.tText, color: offen ? T.body : T.ink, lineHeight: 1.5 }}>{zusammenfassung(auftrag)}</div>
      )}
      {anteil !== null ? <Fortschrittsbalken anteil={anteil} /> : null}

      {auftrag.status === 'teilweise' && auftrag.fehler ? <Hinweis ton="gefahr">{auftrag.fehler}</Hinweis> : null}

      {!offen || auftrag.dokumente?.length ? (
        <AuftragErgebnis auftrag={auftrag} mitPhase={auftrag.art === 'automatik'} />
      ) : null}

      {fehler ? <Hinweis ton="gefahr">{fehler}</Hinweis> : null}

      {kannWiederholen(auftrag) || kannEntfernen(auftrag) ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: `1px solid ${T.hairline}`, paddingTop: 10 }}>
          {kannWiederholen(auftrag) ? (
            <button type="button" onClick={() => onWiederholen(auftrag.id)} disabled={beschaeftigt}
              style={{ ...knopfStil(true), opacity: beschaeftigt ? 0.6 : 1 }}>
              <RotateCcw size={12} />
              {aktion === 'wiederholen' ? 'Wird neu eingereiht …' : 'Erneut versuchen'}
            </button>
          ) : null}
          {kannEntfernen(auftrag) ? (
            <button type="button" onClick={() => onEntfernen(auftrag.id)} disabled={beschaeftigt}
              title="Nur aus dieser Liste – im Deal bleibt alles, wie es ist"
              style={{ ...knopfStil(false), opacity: beschaeftigt ? 0.6 : 1 }}>
              <Trash2 size={12} />
              {aktion === 'entfernen' ? 'Wird entfernt …' : 'Entfernen'}
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
